import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

const StudentLedger = () => {
  const { id } = useParams();

  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [school, setSchool] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [ledgerEntries, setLedgerEntries] = useState([]);

  // ================= FETCH SESSIONS =================
useEffect(() => {
  const fetchSessions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/allSessions`);
      const data = await res.json();

      setSessions(data);

      const currentSessionId = localStorage.getItem("currentSessionId");

      if (currentSessionId) {
        setSelectedSession(currentSessionId);
      } else if (data.length > 0) {
        setSelectedSession(data[0]._id);
      }

    } catch (err) {
      console.error("Session fetch error", err);
    }
  };

  fetchSessions();
}, []);

  // ================= FETCH LEDGER =================
  useEffect(() => {
    const fetchLedger = async () => {
      try {
        const sessionId = localStorage.getItem("currentSessionId");

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ledgers/getLedgerByStudentId/${id}?sessionId=${sessionId}`
        );

        if (response.status === 404) {
          setError("No ledger found for this student.");
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch ledger");
        }

        const data = await response.json();

        if (!data) {
          setError("No ledger found for this student.");
          return;
        }

        setLedger(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load ledger. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchLedger();
  }, [id]);

  // ================= FETCH LEDGER ENTRIES =================
  useEffect(() => {
    const fetchLedgerEntries = async () => {
      try {
        if (!selectedSession || !ledger?._id) return;

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ledgers/ledger-entries/${ledger._id}?sessionId=${selectedSession}`
        );

        const data = await res.json();

        setLedgerEntries(data.entries || []);
      } catch (err) {
        console.error("LedgerEntries error", err);
      }
    };

    fetchLedgerEntries();
  }, [selectedSession, ledger]);

  // ================= FETCH SCHOOL =================
  useEffect(() => {
    const fetchSchool = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/school/getSchool`);
        const data = await res.json();

        if (Array.isArray(data) && data.length > 0) {
          setSchool(data[0]);
        }
      } catch (err) {
        console.error("Failed to fetch school details", err);
      }
    };

    fetchSchool();
  }, []);

  // ================= STATES =================
  if (loading) return <div className="p-6">Loading...</div>;

  if (error)
    return (
      <div style={{ color: "gray", fontSize: "18px", textAlign: "center", marginTop: "60px" }}>
        {error}
      </div>
    );

  // ✅ IMPORTANT FIX HERE
  const entries = ledgerEntries;

  const { studentId, totalAmount } = ledger || {};

  // ================= PDF =================
  const generatePDF = () => {
    const doc = new jsPDF();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(school?.schoolName || "School Name", 14, 20);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(school?.address || "", 14, 28);
    doc.text(`Phone: ${school?.phone1 || ""} | Email: ${school?.email1 || ""}`, 14, 36);

    doc.line(14, 40, 196, 40);

    doc.setFont("helvetica", "bold");
    doc.text("Student Ledger", 14, 50);

    doc.setFont("helvetica", "normal");
    doc.text(`Student ID: ${id}`, 14, 58);
    doc.text(`Total Amount: ₹${totalAmount?.toLocaleString("en-IN")}`, 14, 66);

    doc.autoTable({
      startY: 75,
      head: [["Date", "Receipt No", "Description", "Debit", "Credit", "Miscellaneous", "Balance"]],
      body: entries.map((e) => [
        new Date(e.date).toLocaleDateString(),
        e.receiptNumber || "-",
        e.description,
        e.debitAmount ? `₹${e.debitAmount}` : "-",
        e.creditAmount ? `₹${e.creditAmount}` : "-",
        e.miscellaneousAmount ? `₹${e.miscellaneousAmount}` : "-",
        `₹${e.balance}`,
      ]),
      styles: { fontSize: 9 },
      theme: "grid",
    });

    doc.save(`ledger_${id}.pdf`);
  };

  // ================= PRINT =================
  const printPage = () => {
    const content = document.getElementById("ledgerContent").innerHTML;
    const original = document.body.innerHTML;

    document.body.innerHTML = content;
    window.print();
    document.body.innerHTML = original;
    window.location.reload();
  };

  // ================= UI =================
  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="text-center mb-6">
        {school?.logo && (
          <img src={school.logo} alt="School Logo" className="h-20 mx-auto mb-2" />
        )}

        <h1 className="text-3xl font-bold">{school?.schoolName}</h1>
        <p>{school?.address}</p>
        <p>
          Phone: {school?.phone1}
          {school?.email1 && ` | Email: ${school.email1}`}
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-4">
        Student Ledger (ID: {id})
      </h2>

      {/* SESSION DROPDOWN */}
      <div className="flex justify-end mb-4">
        <select
          value={selectedSession}
          onChange={(e) => setSelectedSession(e.target.value)}
          className="border p-2 rounded"
        >
          {sessions.map((s) => (
            <option key={s._id} value={s._id}>
              {s.sessionName || s.name}
            </option>
          ))}
        </select>
      </div>

      {/* ACTIONS */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <p className="mb-4 text-lg">
          Total Amount: <b>₹{totalAmount?.toLocaleString("en-IN")}</b>
        </p>

        <div className="flex flex-wrap gap-3">
          <Link to={`/students/${studentId}`} className="bg-blue-600 text-white px-4 py-2 rounded">
            View Student Profile
          </Link>

          <Link to={`/students/${id}/fee-structure`} className="bg-green-600 text-white px-4 py-2 rounded">
            View Student Fee Structure
          </Link>

          <button onClick={generatePDF} className="bg-red-600 text-white px-4 py-2 rounded">
            Generate PDF
          </button>

          <button onClick={printPage} className="bg-purple-600 text-white px-4 py-2 rounded">
            Print
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div id="ledgerContent" className="overflow-x-auto">
        <table className="w-full border bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Date</th>
              <th className="border p-2">Receipt</th>
              <th className="border p-2">Description</th>
              <th className="border p-2">Miscellaneous (Separate)</th>
              <th className="border p-2">Debit</th>
              <th className="border p-2">Credit</th>
              <th className="border p-2">Balance</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>

          <tbody>
            {entries.map((e, i) => (
              <tr key={i} className="text-center hover:bg-gray-50">
                <td className="border p-2">{new Date(e.date).toLocaleDateString()}</td>
                <td className="border p-2">{e.receiptNumber || "-"}</td>
                <td className="border p-2">{e.description}</td>
                                <td className="border p-2">
  {e.miscellaneousAmount ? `₹${e.miscellaneousAmount}` : "-"}
</td>
                <td className="border p-2">{e.debitAmount ? `₹${e.debitAmount}` : "-"}</td>
                <td className="border p-2">{e.creditAmount ? `₹${e.creditAmount}` : "-"}</td>

                <td className="border p-2">₹{e.balance}</td>
                <td className="border p-2">
                  {e.receiptNumber && (
                    <Link to={`/fee-receipt-details/${e.receiptNumber}`} className="text-blue-600 underline">
                      View Receipt
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>

        </table>
      </div>
    </div>
  );
};

export default StudentLedger;